// Отложенные изменения (contracts/ui.md, «Действия и подтверждения»): «Удалить» файла выгрузки
// скрывает строку сразу, а запрос уходит, когда тост «Файл удалён · Отменить» закрылся без
// «Отменить». Тосты живут дольше экрана и сеанса, поэтому ждущие изменения привязаны к сеансу
// (T121):
// - «Выйти» и «Выйти на всех устройствах» сначала выполняют их — тем же сеансом, пока он действует;
// - конец сеанса и вход другого пользователя отбрасывают оставшиеся вместе с тостами: ни без
//   сеанса, ни с cookie следующего пользователя запрос не уходит;
// - закрытие и перезагрузка вкладки выполняют их запросом с keepalive: человеку уже сказали
//   «Файл удалён», и запрос переживает страницу.

/** Как выполняется ждущее изменение. */
export interface CommitMode {
  /**
   * Сеанс заканчивается или вкладка закрывается: запрос фоновый — окно «Войдите снова» он
   * не открывает (входить уже незачем) и сеанс не продлевает.
   */
  final: boolean;
  /** Вкладка закрывается: запрос с keepalive переживает страницу. */
  keepalive: boolean;
}

export interface Deferred {
  /** Выполнить изменение. Ошибку показывает сам: тост-ошибка, строка возвращается. */
  commit: (mode: CommitMode) => Promise<void>;
  /** Изменение выполнено или отброшено не по тосту — убрать тост с «Отменить». */
  close: () => void;
}

const pending = new Map<string, Deferred>();

const BY_TOAST: CommitMode = { final: false, keepalive: false };

/**
 * Вкладка закрывается или перезагружается: ждущие изменения уходят запросом с keepalive. Тосты
 * убираются: страница может вернуться из кэша браузера, а отменять уже нечего.
 */
function onPageHide(): void {
  for (const item of take()) {
    item.close();
    void item.commit({ final: true, keepalive: true });
  }
}

let listening = false;

/** Изменение ждёт закрытия тоста; key — номер записи. */
export function defer(key: string, item: Deferred): void {
  pending.set(key, item);
  if (!listening && typeof window !== 'undefined') {
    window.addEventListener('pagehide', onPageHide);
    listening = true;
  }
}

/** «Отменить» в тосте: изменение не выполняется. false — оно уже выполнено или отброшено. */
export function cancelDeferred(key: string): boolean {
  return pending.delete(key);
}

/** Тост закрылся без «Отменить»: выполнить изменение, если оно ещё ждёт. */
export function commitDeferred(key: string): Promise<void> {
  const item = pending.get(key);
  if (!item) return Promise.resolve();
  pending.delete(key);
  return item.commit(BY_TOAST);
}

/** Все ждущие — из очереди: каждое выполняется или отбрасывается один раз. */
function take(): Deferred[] {
  const items = [...pending.values()];
  pending.clear();
  return items;
}

/**
 * «Выйти»: выполнить все ждущие изменения, пока сеанс действует, и убрать их тосты — после
 * выхода отменять уже нечего.
 */
export async function commitAllDeferred(): Promise<void> {
  const items = take();
  for (const item of items) item.close();
  await Promise.allSettled(items.map((item) => item.commit({ final: true, keepalive: false })));
}

/** Сеанс закончился или сменился: ждущие изменения отбрасываются вместе с тостами. */
export function dropAllDeferred(): void {
  for (const item of take()) item.close();
}
